<template>
  <div :class="`${prefixCls}`">
    <a-form
      :label-col="{ span: props.labelCol }"
      :wrapper-col="{ span: props.wrapperCol }"
      label-align="left"
    >
      <a-form-item label="类型">
        <a-select v-model:value="backgroundType">
          <a-select-option :value="0">颜色填充</a-select-option>
          <a-select-option :value="1">图片</a-select-option>
        </a-select>
      </a-form-item>
      <template v-if="backgroundType == 1">
        <a-form-item label="图片">
          <div :class="`${prefixCls}__avatar`">
            <div v-if="imageUrl" :class="`${prefixCls}__avatar-preview`">
              <a-badge class="showImgs">
                <div class="actions">
                  <a-button type="link" class="action-btn" @click="setVisible(true)">
                    <eye-outlined class="action-icon" />
                  </a-button>
                  <a-button type="link" class="action-btn" @click="handleFileUpload">
                    <redo-outlined class="action-icon" />
                  </a-button>
                </div>
                <img style="width: 100%" :src="imageUrl" />
                <template #count>
                  <div class="action-del" @click="handleDel">
                    <close-outlined class="del-icon" />
                  </div>
                </template>
              </a-badge>

              <a-image
                :width="200"
                :style="{ display: 'none' }"
                :preview="{ visible, onVisibleChange: setVisible }"
                :src="imageUrl"
              />
            </div>
            <div v-else :class="`${prefixCls}__avatar-uploader`" @click="handleFileUpload">
              <loading-outlined v-if="loading" />
              <div class="upload-box" v-else>
                <plus-outlined class="upload-icon" />
              </div>
            </div>
          </div>
        </a-form-item>
        <template v-if="imageUrl">
          <a-form-item label="图片缩放">
            <a-select v-model:value="imageScaling">
              <a-select-option :value="3">填充</a-select-option>
              <a-select-option :value="4">等比例</a-select-option>
              <a-select-option :value="0">平铺</a-select-option>
              <a-select-option :value="2">拉伸</a-select-option>
              <a-select-option :value="1">原图</a-select-option>
            </a-select>
          </a-form-item>
          <a-form-item label="平铺效果" v-if="isOnPavingEffect">
            <a-select v-model:value="repeatInfoComposed" :options="pavingEffectList" />
          </a-form-item>
          <a-form-item label="图片位置" v-if="isOnImagePosition">
            <ul :class="`${prefixCls}__square`">
              <li
                v-for="item in imagePositionInfos"
                :key="item"
                :class="{
                  [`${prefixCls}__square_item`]: true,
                  [`${prefixCls}__square_item--active`]: item == repeatInfoComposed,
                }"
                @click="handleRepeatChange(item)"
              ></li>
            </ul>
          </a-form-item>
        </template>
      </template>
      <a-form-item label="背景色">
        <color-picker-v3
          v-model="colorForPicker"
          :pickerType="colorPickerOptions.pickerType"
          :format="colorPickerOptions.format"
          :shape="colorPickerOptions.shape"
          :disableHistory="colorPickerOptions.disableHistory"
          :useType="colorPickerOptions.useType"
          :placement="colorPickerOptions.placement"
          @color-change="handleThemeColorClick"
          @after-color-changed="handleColorChangeStop"
        />
      </a-form-item>
      <template v-if="backgroundType == 1">
        <a-form-item label="锁定" v-if="shouldRenderAttachment">
          <a-select v-model:value="backgroundAttachment" @change="handleBackgroundAttachmentChange">
            <a-select-option value="scroll">默认</a-select-option>
            <a-select-option value="fixed">锁定图片</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="透明度" v-if="shouldRenderOpacity">
          <a-row>
            <a-col :span="15">
              <a-slider
                :min="0"
                :max="100"
                :step="1"
                @change="handleOpacityChange"
                @after-change="handleOpacityChangeStop"
                v-model:value="opacityForSlider"
              />
            </a-col>
            <a-col :span="9">
              <a-input-number
                :min="0"
                :max="100"
                :step="1"
                @change="handleOpacityChange"
                v-model:value="opacityForSlider"
                :formatter="(value) => `${value}%`"
                :parser="(value) => value.replace('%', '')"
                style="margin-left: 16px; width: 80px"
              />
            </a-col>
          </a-row>
        </a-form-item>
      </template>
    </a-form>
  </div>
</template>

<script lang="ts" setup>
  import { ColorPickerV3 } from '../../color-picker-v3';
  import { backgroundProps } from './props';
  import { useBackground, useDesign } from '../../../hooks';
  import {
    PlusOutlined,
    LoadingOutlined,
    EyeOutlined,
    RedoOutlined,
    CloseOutlined,
  } from '@ant-design/icons-vue';
  import { ref } from 'vue';

  const { prefixCls } = useDesign('background');

  const props = defineProps(backgroundProps);

  const colorPickerOptions = ref(props.colorPickerOptions);

  const emit = defineEmits([
    'input',
    'typeChange',
    'imageChange',
    'colorChanging',
    'colorChange',
    'scaleChange',
    'attachmentChange',
    'opacityChanging',
    'opacityChange',
    'repeatChange',
    'dropdownPanelUpdate',
  ]);

  const visible = ref(false);

  const handleCancel = () => {
    visible.value = false;
  };

  const setVisible = (value: boolean) => {
    visible.value = value;
  };

  const {
    loading,
    backgroundType,
    imageUrl,
    imageScaling,
    isOnPavingEffect,
    repeatInfoComposed,
    pavingEffectList,
    isOnImagePosition,
    imagePositionInfos,
    handleRepeatChange,
    colorForPicker,
    shouldRenderAttachment,
    backgroundAttachment,
    shouldRenderOpacity,
    opacityForSlider,
    handleColorChangeStop,
    handleThemeColorClick,
    handleOpacityChange,
    handleOpacityChangeStop,
    handleBackgroundAttachmentChange,
    handleFileUpload,
    handleDel,
  } = useBackground(props);
</script>

<style lang="less">
  @import '../style/index';
</style>
